import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('saas_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Get audio blob for playback (with auth)
export const getCallAudioUrl = async (callId) => {
  try {
    const response = await api.get(`/calls/${callId}/audio`, {
      responseType: 'blob'
    });
    return { url: URL.createObjectURL(response.data), error: null };
  } catch (error) {
    const status = error.response?.status;
    let detail = error.response?.data?.detail || error.message;
    
    // If the response is a blob, try to read it as text to get the error message
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        detail = parsed.detail || detail;
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    let errorMessage = 'Unable to load audio';
    let errorType = 'unknown';
    
    if (status === 404) {
      if (detail?.includes('No audio file')) {
        errorMessage = 'No audio file available for this call';
        errorType = 'no_file';
      } else {
        errorMessage = 'Call not found';
        errorType = 'not_found';
      }
    } else if (status === 401) {
      if (detail?.includes('OneDrive not connected')) {
        errorMessage = 'OneDrive is disconnected. Please reconnect OneDrive in Settings to play this audio.';
        errorType = 'onedrive_disconnected';
      } else if (detail?.includes('token') || detail?.includes('refresh failed')) {
        errorMessage = 'OneDrive session expired. Please reconnect OneDrive in Settings.';
        errorType = 'onedrive_expired';
      } else {
        errorMessage = 'Authentication required';
        errorType = 'auth_required';
      }
    } else if (status === 500) {
      errorMessage = 'Server error while loading audio. Please try again.';
      errorType = 'server_error';
    }
    
    console.error('Audio load error:', { status, detail, errorType });
    return { url: null, error: errorMessage, errorType };
  }
};

// Admin
export const getAdminConfig = () => api.get('/admin/config');
export const getAdminSettings = () => api.get('/admin/settings');
export const updateNavOrder = (navOrder) => api.put('/admin/settings/nav-order', navOrder);
export const verifyAdminUrl = (url) => api.get(`/admin/url/${url}`);

// Departments
export const getDepartments = () => api.get('/departments');
export const createDepartment = (data) => api.post('/departments', data);
export const deleteDepartment = (id) => api.delete(`/departments/${id}`);

// Roles
export const getRoles = (tenantId) => api.get('/roles', { params: { tenant_id: tenantId } });
export const getRole = (id) => api.get(`/roles/${id}`);
export const getAvailablePermissions = () => api.get('/roles/permissions');
export const createRole = (data, createdBy) => api.post('/roles', data, { params: { created_by: createdBy } });
export const updateRole = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole = (id) => api.delete(`/roles/${id}`);
export const duplicateRole = (id, newName, tenantId) => 
  api.post(`/roles/${id}/duplicate`, null, { params: { new_name: newName, tenant_id: tenantId } });

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const getUserByUrl = (url) => api.get(`/users/url/${url}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const toggleUserActive = (id) => api.post(`/users/${id}/toggle-active`);
export const regenerateUserUrl = (id) => api.post(`/users/${id}/regenerate-url`);
export const exportUsers = () => api.get('/users/export', { responseType: 'blob' });
export const getImportTemplate = () => api.get('/users/export/template', { responseType: 'blob' });
export const importUsers = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/users/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// User Archive/Restore/Delete
export const softDeleteUsers = (userIds, reason) => 
  api.post('/users/soft-delete', { user_ids: userIds, reason });
export const getDeletedUsers = (params = {}) => 
  api.get('/users/deleted', { params });
export const getDeletedUserArchivedData = (userId) => 
  api.get(`/users/deleted/${userId}/archived-data`);
export const getDeletedUsersArchivedStats = () => 
  api.get('/users/deleted/archived-stats');
export const restoreDeletedUsers = (userIds) => 
  api.post('/users/restore', userIds);
export const permanentDeleteUsers = (userIds) => 
  api.delete('/users/permanent-delete', { data: userIds });

// Create login-enabled user (tenant user with password)
export const createLoginUser = (data) => api.post('/tenant/users/create', data);

// Update login user (tenant user)
export const updateLoginUser = (userId, data) => api.put(`/tenant/users/${userId}`, data);

// Delete login user (tenant user)
export const deleteLoginUser = (userId) => api.delete(`/tenant/users/${userId}`);

// User Password Management
export const setUserPassword = (userId, data) => api.post(`/users/${userId}/set-password`, data);
export const removeUserPassword = (userId) => api.delete(`/users/${userId}/password`);

// User Avatar
export const uploadUserAvatar = (userId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/users/${userId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteUserAvatar = (userId) => api.delete(`/users/${userId}/avatar`);
export const getUserAvatarUrl = (userId) => `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/avatar/view`;

// Helper to get proper avatar URL - handles both API format and direct URLs
export const getAvatarDisplayUrl = (user) => {
  if (!user) return null;
  
  const userId = user.id || user.user_id;
  const avatarUrl = user.avatar_url || user.profile_picture;
  
  // If no avatar, return null
  if (!avatarUrl) return null;
  
  // If it's already an external URL (http/https), use it directly
  if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
    return avatarUrl;
  }
  
  // If it's a local file path (/app/uploads/...), use the API endpoint
  if (avatarUrl.startsWith('/app/uploads/') || avatarUrl.startsWith('/uploads/')) {
    return userId ? getUserAvatarUrl(userId) : null;
  }
  
  // If it's already an API path, prefix with backend URL
  if (avatarUrl.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${avatarUrl}`;
  }
  
  // Default: use the API endpoint if we have a user ID
  return userId ? getUserAvatarUrl(userId) : avatarUrl;
};

// User Insights
export const getUserInsights = (userId) => api.get(`/users/${userId}/insights`);
export const getBulkUserInsights = () => api.get('/users/insights/bulk');
export const upgradeAllTenantsToEnterprise = () => api.post('/admin/upgrade-all-tenants');

// QA Notes
export const createQANote = (data) => api.post('/qa-notes', data);
export const getQANotes = (params) => api.get('/qa-notes', { params });
export const getQANote = (noteId) => api.get(`/qa-notes/${noteId}`);
export const getQANotesForCall = (callId) => api.get(`/qa-notes/call/${callId}`);
export const getQANotesForUser = (userId) => api.get(`/qa-notes/user/${userId}`);
export const markQANoteRead = (noteId) => api.post(`/qa-notes/${noteId}/mark-read`);
export const getQANotesStats = () => api.get('/qa-notes/stats');

// Call Collaboration & Override
export const overrideFatalErrors = (callId, data) => api.post(`/calls/${callId}/override-fatal`, data);
export const overrideCallCompliance = (callId, data) => api.post(`/calls/${callId}/override-compliance`, data);
export const submitCollaboration = (callId, data) => api.post(`/calls/${callId}/collaborate`, data);
export const getCollaborationHistory = (callId) => api.get(`/calls/${callId}/collaboration-history`);

// Call Review Requests
export const createReviewRequest = (data, userId, userName) => 
  api.post('/review-requests', data, { params: { user_id: userId, user_name: userName } });
export const getReviewRequests = (params) => api.get('/review-requests', { params });
export const getReviewRequestStats = () => api.get('/review-requests/stats');
export const getReviewRequest = (requestId) => api.get(`/review-requests/${requestId}`);
export const actionReviewRequest = (requestId, data, reviewerId, reviewerName, reviewerRole) => 
  api.post(`/review-requests/${requestId}/action`, data, { 
    params: { reviewer_id: reviewerId, reviewer_name: reviewerName, reviewer_role: reviewerRole } 
  });
export const getUserReviewRequests = (userId, status) => 
  api.get(`/review-requests/user/${userId}`, { params: { status } });
export const cancelReviewRequest = (requestId, userId) => 
  api.delete(`/review-requests/${requestId}`, { params: { user_id: userId } });

// Process Tracking
export const getActiveProcesses = (includeCompleted = true) => 
  api.get('/processes/active', { params: { include_completed: includeCompleted } });
export const getProcessStatus = (processId) => api.get(`/processes/${processId}`);
export const dismissProcess = (processId) => api.delete(`/processes/${processId}`);
export const clearCompletedProcesses = () => api.delete('/processes/clear/completed');

// Call Audit Trail
export const logCallAudit = (data, actorId, actorName, actorRole = 'user', actorType = 'user') =>
  api.post('/call-audit', data, { params: { actor_id: actorId, actor_name: actorName, actor_role: actorRole, actor_type: actorType } });
export const getCallAuditTrail = (callId, params = {}) => api.get(`/call-audit/call/${callId}`, { params });
export const getCallAuditSummary = (callId) => api.get(`/call-audit/call/${callId}/summary`);
export const getUserAuditTrail = (userId, params = {}) => api.get(`/call-audit/user/${userId}`, { params });
export const getRecentAuditEntries = (params = {}) => api.get('/call-audit/recent', { params });
export const getAuditOverallStats = () => api.get('/call-audit/stats');

// Location Management
export const getLocations = (params) => api.get('/locations', { params });
export const getLocation = (locationId) => api.get(`/locations/${locationId}`);
export const createLocation = (data) => api.post('/locations', data);
export const updateLocation = (locationId, data) => api.put(`/locations/${locationId}`, data);
export const deleteLocation = (locationId, force = false) => api.delete(`/locations/${locationId}`, { params: { force } });
export const restoreLocation = (locationId) => api.post(`/locations/${locationId}/restore`);

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadNotificationCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (notificationId) => api.post(`/notifications/${notificationId}/mark-read`);
export const markAllNotificationsRead = () => api.post('/notifications/mark-all-read');

// Audit Logs & Tracking
export const getQAAuditLogs = (params) => api.get('/audit-logs', { params });
export const trackUserAction = (action, page, entityType = null, entityId = null, details = null) => 
  api.post('/audit/track', { action, page, entity_type: entityType, entity_id: entityId, details });
export const getAuditLogs = (params) => api.get('/audit/logs', { params });
export const getAuditStats = () => api.get('/audit/stats');

// Processing Control
export const stopProcessing = () => api.post('/onedrive/stop-processing');
export const hardStopProcessing = () => api.post('/onedrive/hard-stop');
export const resumeProcessing = () => api.post('/onedrive/resume-processing');
export const clearProcessingQueue = () => api.post('/onedrive/clear-queue');
export const getProcessingStatus = () => api.get('/onedrive/processing-status');

// Activity Logs
export const logActivity = (action, userId, details = null) => 
  api.post('/activity/log', null, { params: { action, user_id: userId, details } });
export const getActivityLogs = (params) => api.get('/activity/logs', { params });
export const getUserActivity = (userId, limit = 50) => 
  api.get(`/activity/user/${userId}`, { params: { limit } });

// Session Tracking
export const sendSessionHeartbeat = (data) => api.post('/activity/session/heartbeat', data);
export const getTodaySessions = () => api.get('/activity/sessions/today');
export const getUserSessions = (userId, days = 7) => 
  api.get(`/activity/sessions/user/${userId}`, { params: { days } });
export const getOnlineUsers = () => api.get('/activity/online-users');

// Calls
export const getCalls = (params) => api.get('/calls', { params });
export const getCall = (id) => api.get(`/calls/${id}`);
export const regenerateExpertReport = (id) => api.post(`/calls/${id}/regenerate-expert-report`);
export const getCallStatistics = (params) => api.get('/calls/statistics', { params });
export const getUnassignedCalls = () => api.get('/calls/unassigned/list');
export const getRelatedCallsByPhone = (phoneNumber, params = {}) => 
  api.get(`/calls/related/by-phone/${encodeURIComponent(phoneNumber)}`, { params });
export const assignCall = (callId, userId) => api.post(`/calls/${callId}/assign/${userId}`);
export const analyzeCall = (callId) => api.post(`/calls/${callId}/analyze`);
export const getComplianceFilters = () => api.get('/calls/compliance-filters');
export const getCallStatus = (callId) => api.get(`/calls/${callId}/status`);
export const uploadCallFile = (file, userId, tenantId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (userId) formData.append('user_id', userId);
  if (tenantId) formData.append('tenant_id', tenantId);
  return api.post('/calls/upload-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const findDuplicateCalls = (tenantId) => 
  api.get('/calls/duplicates', { params: tenantId ? { tenant_id: tenantId } : {} });
export const cleanupDuplicateCalls = (tenantId, dryRun = true) => 
  api.get('/calls/duplicates/cleanup', { params: { ...(tenantId ? { tenant_id: tenantId } : {}), dry_run: dryRun } });

// Call Soft Delete
export const softDeleteCalls = (callIds, reason) => 
  api.post('/calls/soft-delete', { call_ids: callIds, reason });
export const getDeletedCalls = (params) => api.get('/calls/deleted', { params });
export const restoreDeletedCalls = (callIds) => api.post('/calls/restore', callIds);
export const permanentDeleteCalls = (callIds) => api.delete('/calls/permanent-delete', { data: callIds });
export const getDeletedCallArchivedData = (callId) => api.get(`/calls/deleted/${callId}/archived-data`);
export const getDeletedCallsArchivedStats = () => api.get('/calls/deleted/archived-stats');

// Data Retention Policy
export const getGlobalRetentionPolicy = () => api.get('/retention-policy/global');
export const updateGlobalRetentionPolicy = (data) => api.put('/retention-policy/global', data);
export const getTenantRetentionPolicy = () => api.get('/retention-policy/tenant');
export const updateTenantRetentionPolicy = (data) => api.put('/retention-policy/tenant', data);
export const resetTenantRetentionPolicy = () => api.delete('/retention-policy/tenant');
export const getPendingPurgeData = () => api.get('/retention-policy/pending-purge');
export const triggerManualPurge = () => api.post('/retention-policy/purge-now');
export const getPurgeLogs = (params) => api.get('/retention-policy/purge-logs', { params });

// Call Type Refresh
export const refreshCallTypes = (callIds) => api.post('/calls/refresh-call-types', { call_ids: callIds });

// Unknown Calls Management
export const getUnknownCalls = (params) => api.get('/calls/unknown', { params });
export const getUnknownCallsStats = () => api.get('/calls/unknown/stats');
export const syncUnknownCallsWithUsers = () => api.post('/calls/sync-users');
export const assignCallToUser = (callId, userId) => api.post(`/calls/${callId}/assign-user?user_id=${userId}`);
export const markCallAsUnknown = (callId) => api.post(`/calls/${callId}/mark-unknown`);
export const syncSingleCall = (callId) => api.post(`/calls/${callId}/sync`);
export const updateCallFormat = (callId, data) => api.put(`/calls/${callId}/format`, data);

// Transcript Regeneration
export const regenerateTranscript = (callIds, requestedBy) => 
  api.post('/reprocess/regenerate-transcript', { call_ids: callIds }, { params: { requested_by: requestedBy } });
export const getTranscriptRegenStatus = () => api.get('/reprocess/regenerate-transcript/status');

// Documents
export const getDocuments = (params) => api.get('/documents', { params });
export const getDocument = (id) => api.get(`/documents/${id}`);
export const uploadDocument = (file, metadata) => {
  const formData = new FormData();
  formData.append('file', file);
  if (metadata?.description) formData.append('description', metadata.description);
  if (metadata?.category) formData.append('category', metadata.category);
  if (metadata?.is_visible !== undefined) formData.append('is_visible', metadata.is_visible);
  return api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const updateDocument = (id, data) => api.put(`/documents/${id}`, null, { params: data });
export const toggleDocumentVisibility = (id) => api.post(`/documents/${id}/toggle-visibility`);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const getDocumentCategories = () => api.get('/documents/categories');
export const getStorageConfig = () => api.get('/documents/storage-config');
export const updateStorageConfig = (storageType) => api.put('/documents/storage-config', null, { params: { active_storage: storageType } });

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getLeaderboard = (period) => api.get('/dashboard/leaderboard', { params: { period } });
export const getUserStats = (userId) => api.get(`/dashboard/user/${userId}/stats`);

// Scoring Configuration
export const getScoringConfig = () => api.get('/scoring/config');
export const updateScoringConfig = (config) => api.put('/scoring/config', config);
export const addScoringParameter = (param) => api.post('/scoring/parameter', param);
export const updateScoringParameter = (id, param) => api.put(`/scoring/parameter/${id}`, param);
export const deleteScoringParameter = (id) => api.delete(`/scoring/parameter/${id}`);
export const addFatalParameter = (param) => api.post('/scoring/fatal-parameter', param);
export const deleteFatalParameter = (id) => api.delete(`/scoring/fatal-parameter/${id}`);
export const updateClassificationThresholds = (thresholds) => api.put('/scoring/thresholds', thresholds);
export const resetScoringConfig = () => api.post('/scoring/reset');
export const seedUniversalAIScoringGuide = () => api.post('/scoring/configs/seed-universal-ai-guide');

// API Configuration
export const getAPIConfigs = () => api.get('/api-configs');
export const getAPIConfig = (id) => api.get(`/api-configs/${id}`);
export const createAPIConfig = (config) => api.post('/api-configs', config);
export const updateAPIConfig = (id, config) => api.put(`/api-configs/${id}`, config);
export const deleteAPIConfig = (id) => api.delete(`/api-configs/${id}`);
export const verifyAPIConfig = (id) => api.post(`/api-configs/${id}/verify`);
export const testAPICall = (id, payload) => api.post(`/api-configs/${id}/test-call`, payload);
export const uploadAPIDoc = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api-configs/${id}/upload-doc`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Call Log Notes & Compliance
export const getCallLogNotes = (params) => api.get('/call-log-notes', { params });
export const getCallLogComplianceStats = (params) => api.get('/call-log-notes/compliance-stats', { params });
export const getComplianceByUser = () => api.get('/call-log-notes/compliance-by-user');
export const getComplianceAlerts = () => api.get('/call-log-notes/alerts');
export const syncCallLogNotes = () => api.post('/call-log-notes/sync');
export const syncCallLogNotesFromCalls = () => api.post('/call-log-notes/sync-from-calls');
export const seedCallLogNotes = () => api.post('/call-log-notes/seed-demo');
export const updateCallLogNote = (logId, data) => api.put(`/call-log-notes/${logId}`, data);

// Notes Compliance - Enhanced Sync
export const getCallLogSyncStatus = () => api.get('/call-log-notes/sync-status');
export const syncCallLogNotesEnhanced = (options) => api.post('/call-log-notes/sync-enhanced', options);
export const getCallLogSyncHistory = (params) => api.get('/call-log-notes/sync-history', { params });
export const clearCallLogSyncHistory = (olderThanDays = 30) => api.delete('/call-log-notes/sync-history', { params: { older_than_days: olderThanDays } });

// Coaching
export const getUserCoaching = (userId) => api.get(`/coaching/${userId}`);
export const createCoaching = (data) => api.post('/coaching', data);
export const getAICoachingPlan = (userId) => api.get(`/coaching/ai-plan/${userId}`);
export const getCompanyCoachingStats = (params) => api.get('/coaching/company-stats', { params });

// Leaderboard
export const getLeaderboardData = (params) => api.get('/leaderboard', { params });
export const getUserLeaderboardPosition = (userId) => api.get(`/leaderboard/user/${userId}`);
export const setLeaderboardOverride = (userId, category, action, note) => 
  api.post('/leaderboard/override', null, { params: { user_id: userId, category, action, note } });
export const removeLeaderboardOverride = (userId, category) => 
  api.delete(`/leaderboard/override/${userId}/${category}`);

// Applicant Search
export const getSearchLimits = () => api.get('/search-limits');
export const updateSearchLimits = (userLimit, adminLimit) => 
  api.put('/search-limits', null, { params: { user_daily_limit: userLimit, admin_daily_limit: adminLimit } });
export const getSearchUsage = (searcherId, searcherType) => 
  api.get(`/search-usage/${searcherId}`, { params: { searcher_type: searcherType } });
export const searchApplicants = (phoneNumber, searcherId, searcherType, dateFilter, startDate, endDate, searchSources = 'calls') =>
  api.post('/search-applicants', null, { 
    params: { 
      phone_number: phoneNumber, 
      searcher_id: searcherId, 
      searcher_type: searcherType,
      date_filter: dateFilter,
      start_date: startDate,
      end_date: endDate,
      search_sources: searchSources
    } 
  });

// Search Lock Management
export const getSearchStatus = () => api.get('/search-status');
export const acquireSearchLock = (phoneNumber, searcherId) => 
  api.post('/search-lock/acquire', null, { params: { phone_number: phoneNumber, searcher_id: searcherId } });
export const releaseSearchLock = (searcherId) => 
  api.post('/search-lock/release', null, { params: { searcher_id: searcherId } });

// Audio Decoding Formats
export const getAudioDecodingFormats = () => api.get('/audio-decoding-formats');
export const getAudioDecodingFormat = (id) => api.get(`/audio-decoding-formats/${id}`);
export const createAudioDecodingFormat = (data) => 
  api.post('/audio-decoding-formats', null, { 
    params: { 
      name: data.name,
      call_type: data.call_type,
      pattern_description: data.pattern_description,
      segments: JSON.stringify(data.segments),
      example_filename: data.example_filename
    } 
  });
export const updateAudioDecodingFormat = (id, data) => 
  api.put(`/audio-decoding-formats/${id}`, null, { 
    params: { 
      name: data.name,
      call_type: data.call_type,
      pattern_description: data.pattern_description,
      segments: data.segments ? JSON.stringify(data.segments) : undefined,
      example_filename: data.example_filename,
      is_active: data.is_active
    } 
  });
export const deleteAudioDecodingFormat = (id) => api.delete(`/audio-decoding-formats/${id}`);
export const parseFilenameWithFormat = (filename, formatId) => 
  api.post('/audio-decoding-formats/parse', null, { params: { filename, format_id: formatId } });
export const seedDefaultFormats = () => api.post('/audio-decoding-formats/seed-defaults');

// OneDrive
export const getOneDriveAuthUrl = () => api.get('/onedrive/auth-url');
export const oneDriveCallback = (code) => api.post('/onedrive/callback', null, { params: { code } });
export const listOneDriveFolders = (folderId) => api.get('/onedrive/folders', { params: { folder_id: folderId } });
export const selectOneDriveFolder = (folderId, folderPath) => api.post('/onedrive/select-folder', null, { params: { folder_id: folderId, folder_path: folderPath } });
export const syncOneDrive = (recursive = true, initiatedBy = 'user') => api.post('/onedrive/sync', null, { params: { recursive, initiated_by: initiatedBy } });
export const getOneDriveSyncStatus = () => api.get('/onedrive/sync-status');
export const getOneDriveConnectionStatus = () => api.get('/onedrive/connection-status');
export const getOneDriveSyncHistory = (params = {}) => api.get('/onedrive/sync-history', { params });
export const getOneDriveSyncHistoryDetail = (syncId) => api.get(`/onedrive/sync-history/${syncId}`);
export const clearOneDriveSyncHistory = (olderThanDays = 30) => api.delete('/onedrive/sync-history', { params: { older_than_days: olderThanDays } });
export const diagnoseOneDrive = () => api.get('/onedrive/diagnose');
export const forceRefreshOneDriveToken = () => api.post('/onedrive/force-refresh-token');
export const repairFolderSelection = () => api.post('/onedrive/repair-folder-selection');
export const getAzureConfig = () => api.get('/onedrive/azure-config');
export const saveAzureConfig = (config) => api.post('/onedrive/azure-config', config);
export const testAzureConnection = () => api.post('/onedrive/test-connection');
export const disconnectOneDrive = () => api.post('/onedrive/disconnect');
export const getFolderSyncStatus = () => api.get('/onedrive/folder-sync-status');
export const getOneDriveDashboardStatus = () => api.get('/onedrive/dashboard-status');
export const traceMissingCalls = (params) => api.get('/calls/trace-missing', { params });
export const getFolderInfo = (folderId) => api.get(`/onedrive/folder-info/${folderId}`);

// Call Processing
export const getCallsProcessingStatus = () => api.get('/calls/processing-status');
export const processPendingCalls = () => api.post('/calls/process-pending');
export const retryFailedCalls = () => api.post('/calls/retry-failed');
export const forceReprocessAllCalls = () => api.post('/calls/force-reprocess-all');
export const resetStuckCalls = () => api.post('/calls/reset-stuck');
export const stopCallsProcessing = () => api.post('/calls/stop-processing');
export const clearStopFlag = () => api.post('/calls/clear-stop-flag');
export const getDebugFlags = () => api.get('/calls/debug-flags');
export const getNetworkDiagnostics = () => api.get('/calls/network-diagnostics');
export const getProcessingQueueDetails = (page = 1, limit = 500, statusFilter = null) => 
  api.get('/calls/processing-queue-details', { 
    params: { page, limit, status_filter: statusFilter } 
  });

// Master Delete
export const getMasterStats = () => api.get('/master/stats');
export const masterDelete = (data) => api.post('/master/delete', data);
export const resetAllStats = (password) => api.post('/master/reset-stats', { password });
export const getDeleteHistory = (page = 1, pageSize = 20) => 
  api.get('/master/delete-history', { params: { page, page_size: pageSize } });

// Homework AI
export const getUserHomework = (userId, status = null, week = null) => 
  api.get(`/homework/user/${userId}`, { params: { status, week } });
export const getUserHomeworkProgress = (userId) => api.get(`/homework/user/${userId}/progress`);
export const assignHomework = (data) => api.post('/homework/assign', data);
export const assignHomeworkFromCoaching = (userId) => api.post(`/homework/assign-from-coaching/${userId}`);
export const startHomeworkTask = (taskId) => api.post(`/homework/${taskId}/start`);
export const completeHomeworkTask = (taskId, data) => api.post(`/homework/${taskId}/complete`, data);
export const getHomeworkAIReview = (taskId) => api.post(`/homework/${taskId}/ai-review`);
export const deleteHomeworkTask = (taskId) => api.delete(`/homework/${taskId}`);
export const getAdminHomeworkOverview = () => api.get('/homework/admin/overview');
export const getAdminUserHomework = (userId) => api.get(`/homework/admin/user/${userId}`);
export const getHomeworkStats = () => api.get('/homework/stats');

// Library
export const getLibrarySections = () => api.get('/library/sections');
export const createLibrarySection = (name, description, icon) => 
  api.post('/library/sections', null, { params: { name, description, icon } });
export const updateLibrarySection = (sectionId, data) => 
  api.put(`/library/sections/${sectionId}`, null, { params: data });
export const deleteLibrarySection = (sectionId) => api.delete(`/library/sections/${sectionId}`);
export const getLibraryCalls = (sectionId = null, limit = 50, skip = 0) => 
  api.get('/library/calls', { params: { section_id: sectionId, limit, skip } });
export const addCallToLibrary = (callId, sectionIds, adminNotes, isFeatured) => 
  api.post('/library/calls/add', { call_id: callId, section_ids: sectionIds, admin_notes: adminNotes, is_featured: isFeatured });
export const toggleLibraryCallVisibility = (libraryCallId, isHidden) => 
  api.post(`/library/calls/${libraryCallId}/hide`, null, { params: { is_hidden: isHidden } });
export const toggleLibraryCallFeatured = (libraryCallId, isFeatured) => 
  api.post(`/library/calls/${libraryCallId}/feature`, null, { params: { is_featured: isFeatured } });
export const removeCallFromLibrary = (libraryCallId) => api.delete(`/library/calls/${libraryCallId}`);
export const searchLibrary = (query, userId = null, limit = 10, skip = 0) => 
  api.get('/library/search', { params: { query, user_id: userId, limit, skip } });
export const logLibraryAccess = (libraryCallId, userId, action) => 
  api.post(`/library/calls/${libraryCallId}/access`, null, { params: { user_id: userId, action } });
export const getLibraryAdminOverview = () => api.get('/library/admin/stats');
export const getAdminLibraryCalls = (includeHidden = true) => 
  api.get('/library/admin/calls', { params: { include_hidden: includeHidden } });
export const getEligibleCallsForLibrary = () => api.get('/library/eligible-calls');

// KPI
export const getKPIPenaltyTypes = () => api.get('/kpi/penalty-types');
export const getKPIConfig = () => api.get('/kpi/config');
export const updateKPIConfig = (config) => api.put('/kpi/config', config);
export const getUserKPI = (userId, month = null, year = null) => 
  api.get(`/kpi/user/${userId}`, { params: { month, year } });
export const recalculateUserKPI = (userId, month = null, year = null) => 
  api.post(`/kpi/user/${userId}/recalculate`, null, { params: { month, year } });
export const getUserKPIHistory = (userId, limit = 12) => 
  api.get(`/kpi/user/${userId}/history`, { params: { limit } });
export const getCompanyKPI = (params) => api.get('/kpi/company', { params });
export const calculateAllKPIs = (month = null, year = null) => 
  api.post('/kpi/calculate-all', null, { params: { month, year } });
export const exportKPIReport = (params) => api.get('/kpi/export', { params, responseType: 'blob' });
export const getKPIFilters = () => api.get('/kpi/filters');

// Multi-Model KPI Configuration
export const getKPIConfigs = () => api.get('/kpi/configs');
export const createKPIConfig = (config) => api.post('/kpi/configs', config);
export const getKPIConfigById = (configId) => api.get(`/kpi/configs/${configId}`);
export const updateKPIConfigById = (configId, updates) => api.put(`/kpi/configs/${configId}`, updates);
export const deleteKPIConfigById = (configId) => api.delete(`/kpi/configs/${configId}`);
export const duplicateKPIConfig = (configId, newName) => 
  api.post(`/kpi/configs/${configId}/duplicate`, null, { params: { new_name: newName } });
export const setKPIConfigDefault = (configId) => api.post(`/kpi/configs/${configId}/set-default`);
export const getKPIConfigForUser = (userId) => api.get(`/kpi/configs/for-user/${userId}`);
export const getKPIAssignments = () => api.get('/kpi/assignments');
export const createKPIAssignment = (assignment) => api.post('/kpi/assignments', assignment);
export const createBulkKPIAssignments = (bulk) => api.post('/kpi/assignments/bulk', bulk);
export const deleteKPIAssignment = (assignmentId) => api.delete(`/kpi/assignments/${assignmentId}`);

// Multi-Model Scoring Configuration
export const getScoringConfigs = () => api.get('/scoring/configs');
export const createScoringConfig = (config) => api.post('/scoring/configs', config);
export const getScoringConfigById = (configId) => api.get(`/scoring/configs/${configId}`);
export const updateScoringConfigById = (configId, updates) => api.put(`/scoring/configs/${configId}`, updates);
export const deleteScoringConfigById = (configId) => api.delete(`/scoring/configs/${configId}`);
export const duplicateScoringConfig = (configId, newName) => 
  api.post(`/scoring/configs/${configId}/duplicate`, null, { params: { new_name: newName } });
export const setScoringConfigDefault = (configId) => api.post(`/scoring/configs/${configId}/set-default`);
export const getScoringConfigForUser = (userId) => api.get(`/scoring/configs/for-user/${userId}`);
export const getScoringAssignments = () => api.get('/scoring/assignments');
export const createScoringAssignment = (assignment) => api.post('/scoring/assignments', assignment);
export const createBulkScoringAssignments = (bulk) => api.post('/scoring/assignments/bulk', bulk);
export const deleteScoringAssignment = (assignmentId) => api.delete(`/scoring/assignments/${assignmentId}`);

// TW Scoring Guide Management
export const seedTWScoringGuide = () => api.post('/scoring/configs/seed-tw-guide');
export const getAvailableScoringGuides = () => api.get('/scoring/guides/available');
export const getTWCallTypes = () => api.get('/scoring/call-types');

// Demo
export const seedDemoData = () => api.post('/demo/seed');
export const seedCallLogDemo = () => api.post('/call-log-notes/seed-demo');

// API Key Management (Tenant)
export const getApiKeys = () => api.get('/api-keys/');
export const createApiKey = (data) => api.post('/api-keys/', data);
export const getApiKey = (id) => api.get(`/api-keys/${id}`);
export const updateApiKey = (id, data) => api.put(`/api-keys/${id}`, data);
export const deleteApiKey = (id) => api.delete(`/api-keys/${id}`);
export const regenerateApiKey = (id) => api.post(`/api-keys/${id}/regenerate`);
export const revokeApiKey = (id) => api.post(`/api-keys/${id}/revoke`);

// Audit Logs (Tenant - Compliance)
export const getComplianceAuditLogs = (params) => api.get('/compliance/audit-logs', { params });
export const getAuditLogActions = () => api.get('/compliance/audit-logs/actions');
export const getAuditLogResourceTypes = () => api.get('/compliance/audit-logs/resource-types');
export const exportAuditLogs = (params) => api.get('/compliance/audit-logs/export', { params, responseType: 'blob' });
export const getComplianceSettings = () => api.get('/compliance/settings');
export const updateComplianceSettings = (data) => api.put('/compliance/settings', data);

// Call Assignment Configuration
export const getCallAssignmentConfig = (tenantId) => api.get('/call-assignment/config', { params: { tenant_id: tenantId } });
export const updateCallAssignmentConfig = (config) => api.put('/call-assignment/config', config);
export const resetCallAssignmentConfig = (tenantId) => api.post('/call-assignment/config/reset', null, { params: { tenant_id: tenantId } });
export const testCallAssignmentMatch = (data) => api.post('/call-assignment/test-match', data);
export const refreshAllCallsAssignment = (tenantId) => api.post('/call-assignment/refresh-all', null, { params: { tenant_id: tenantId } });

// Process Configuration & Call Types
export const getCallTypes = (processType = 'staffing') => api.get('/process-config/call-types', { params: { process_type: processType } });
export const getCallTypeParameters = (callType, processType = 'staffing') => api.get(`/process-config/call-type/${callType}/parameters`, { params: { process_type: processType } });
export const getTenantProcessConfig = () => api.get('/process-config/tenant');
export const updateTenantProcessConfig = (config) => api.put('/process-config/tenant', config);
export const overrideCallType = (callId, callType, reason) => api.put(`/calls/${callId}/call-type`, { call_id: callId, call_type: callType, reason });

export default api;

// Analytics Dashboard
export const getCallTypeTrends = (tenantId, period = '30d', filters = {}) => {
  const params = { tenant_id: tenantId, period };
  if (filters.userIds?.length) params.user_ids = filters.userIds.join(',');
  if (filters.userId) params.user_id = filters.userId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return api.get('/analytics/call-type-trends', { params });
};
export const getCallTypeDistribution = (tenantId, period = '30d', filters = {}) => {
  const params = { tenant_id: tenantId, period };
  if (filters.userIds?.length) params.user_ids = filters.userIds.join(',');
  if (filters.userId) params.user_id = filters.userId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return api.get('/analytics/call-type-distribution', { params });
};
export const getUserPerformanceComparison = (tenantId, period = '30d', metric = 'score', filters = {}) => {
  const params = { tenant_id: tenantId, period, metric };
  if (filters.userIds?.length) params.user_ids = filters.userIds.join(',');
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return api.get('/analytics/user-performance-comparison', { params });
};
export const getUserTrends = (userId, tenantId, period = '30d') => 
  api.get(`/analytics/user-trends/${userId}`, { params: { tenant_id: tenantId, period } });
export const getScoreDistribution = (tenantId, period = '30d', filters = {}) => {
  const params = { tenant_id: tenantId, period };
  if (filters.userIds?.length) params.user_ids = filters.userIds.join(',');
  if (filters.userId) params.user_id = filters.userId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return api.get('/analytics/score-distribution', { params });
};
export const getActivityHeatmap = (tenantId, period = '30d', filters = {}) => {
  const params = { tenant_id: tenantId, period };
  if (filters.userIds?.length) params.user_ids = filters.userIds.join(',');
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return api.get('/analytics/activity-heatmap', { params });
};
export const getAnalyticsDashboardSummary = (tenantId, period = '30d', filters = {}) => {
  const params = { tenant_id: tenantId, period };
  if (filters.userIds?.length) params.user_ids = filters.userIds.join(',');
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return api.get('/analytics/dashboard-summary', { params });
};
export const getAnalyticsLeaderboard = (tenantId, period = '30d', metric = 'score', limit = 10, filters = {}) => {
  const params = { tenant_id: tenantId, period, metric, limit };
  if (filters.userIds?.length) params.user_ids = filters.userIds.join(',');
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  return api.get('/analytics/leaderboard', { params });
};
export const exportAnalyticsCSV = (tenantId, reportType = 'user_performance', period = '30d') => 
  api.get('/analytics/export/csv', { params: { tenant_id: tenantId, report_type: reportType, period }, responseType: 'blob' });

// Case Studies API
export const getCaseStudies = (tenantId, userId, params = {}) =>
  api.get('/case-studies/', { params: { tenant_id: tenantId, user_id: userId, ...params } });
export const getCaseStudy = (caseStudyId, userId) =>
  api.get(`/case-studies/${caseStudyId}`, { params: { user_id: userId } });
export const createCaseStudy = (tenantId, userId, data) =>
  api.post('/case-studies/', data, { params: { tenant_id: tenantId, user_id: userId } });
export const updateCaseStudy = (caseStudyId, userId, data) =>
  api.put(`/case-studies/${caseStudyId}`, data, { params: { user_id: userId } });
export const deleteCaseStudy = (caseStudyId, userId) =>
  api.delete(`/case-studies/${caseStudyId}`, { params: { user_id: userId } });
export const searchCallsForCaseStudy = (tenantId, params = {}) =>
  api.get('/case-studies/search/calls', { params: { tenant_id: tenantId, ...params } });
export const uploadCallToCaseStudy = (caseStudyId, userId, formData) =>
  api.post(`/case-studies/${caseStudyId}/upload-call`, formData, {
    params: { user_id: userId },
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const shareCaseStudy = (caseStudyId, userId, shareData) =>
  api.post(`/case-studies/${caseStudyId}/share`, shareData, { params: { user_id: userId } });
export const revokeCaseStudyAccess = (caseStudyId, targetUserId, userId) =>
  api.delete(`/case-studies/${caseStudyId}/share/${targetUserId}`, { params: { user_id: userId } });
export const generateCaseStudyAnalysis = (caseStudyId, userId) =>
  api.post(`/case-studies/${caseStudyId}/generate`, null, { params: { user_id: userId } });
export const exportCaseStudy = (caseStudyId, userId, format = 'markdown') =>
  api.get(`/case-studies/${caseStudyId}/export`, { params: { user_id: userId, format } });

// Call Re-Processing API
export const getReprocessStatus = () => api.get('/reprocess/status');
export const getReprocessScoringGuides = () => api.get('/reprocess/scoring-guides');
export const reprocessCall = (callId, options = {}) =>
  api.post(`/reprocess/call/${callId}`, options);
export const queueCallsForReprocess = (tenantId, data, requestedBy) =>
  api.post('/reprocess/queue', data, { params: { requested_by: requestedBy } });
export const startReprocessing = (tenantId, requestedBy) =>
  api.post('/reprocess/start', null, { params: { tenant_id: tenantId, requested_by: requestedBy } });
export const stopReprocessing = () => api.post('/reprocess/stop');
export const hardStopReprocessing = () => api.post('/reprocess/hard-stop');
export const clearReprocessQueue = (tenantId) =>
  api.delete('/reprocess/queue', { params: { tenant_id: tenantId } });
export const getReprocessStats = (tenantId) =>
  api.get('/reprocess/stats', { params: { tenant_id: tenantId } });

// DOB/SSN Exceptions API
export const getDOBSSNExceptions = (tenantId) =>
  api.get('/settings/dob-ssn-exceptions', { params: { tenant_id: tenantId } });
export const addDOBSSNException = (tenantId, data) =>
  api.post('/settings/dob-ssn-exceptions', data, { params: { tenant_id: tenantId } });
export const removeDOBSSNException = (tenantId, exceptionId) =>
  api.delete(`/settings/dob-ssn-exceptions/${exceptionId}`, { params: { tenant_id: tenantId } });
export const checkUserDOBSSNException = (tenantId, userId) =>
  api.get(`/settings/dob-ssn-exceptions/check/${userId}`, { params: { tenant_id: tenantId } });

// ==================== SUBMISSIONS API ====================

// Configuration
export const getSubmissionsConfig = (tenantId) =>
  api.get('/submissions/config', { params: { tenant_id: tenantId } });
export const saveSubmissionsConfig = (tenantId, config) =>
  api.post('/submissions/config', config, { params: { tenant_id: tenantId } });
export const updateSubmissionsConfig = (tenantId, config) =>
  api.put('/submissions/config', config, { params: { tenant_id: tenantId } });
export const testSubmissionsConnection = (tenantId) =>
  api.post('/submissions/config/test', null, { params: { tenant_id: tenantId } });
export const updateColumnVisibility = (tenantId, columns) =>
  api.put('/submissions/config/columns', columns, { params: { tenant_id: tenantId } });

// Sync
export const triggerSubmissionsSync = (tenantId, fullSync = false) =>
  api.post('/submissions/sync', null, { params: { tenant_id: tenantId, full_sync: fullSync } });
export const getSubmissionsSyncStatus = (tenantId) =>
  api.get('/submissions/sync/status', { params: { tenant_id: tenantId } });

// Submissions Data
export const getSubmissions = (tenantId, params = {}) =>
  api.get('/submissions', { params: { tenant_id: tenantId, ...params } });
export const getSubmissionDetail = (tenantId, submissionId) =>
  api.get(`/submissions/${submissionId}`, { params: { tenant_id: tenantId } });
export const analyzeSubmission = (tenantId, submissionId) =>
  api.post(`/submissions/${submissionId}/analyze`, null, { params: { tenant_id: tenantId } });
export const createManualSubmission = (tenantId, data) =>
  api.post('/submissions/manual', data, { params: { tenant_id: tenantId } });
export const createManualSubmissionWithFile = (tenantId, formData) =>
  api.post('/submissions/manual/with-file', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' }
  });

// Dashboard
export const getSubmissionsDashboardStats = (tenantId, params = {}) =>
  api.get('/submissions/dashboard/stats', { params: { tenant_id: tenantId, ...params } });
export const getSubmissionsRecruiterStats = (tenantId, params = {}) =>
  api.get('/submissions/dashboard/recruiters', { params: { tenant_id: tenantId, ...params } });
export const getSubmissionsCallCorrelation = (tenantId, params = {}) =>
  api.get('/submissions/dashboard/call-correlation', { params: { tenant_id: tenantId, ...params } });

// Phone matching
export const checkPhoneForSubmissions = (tenantId, phone) =>
  api.get(`/submissions/check-phone/${encodeURIComponent(phone)}`, { params: { tenant_id: tenantId } });
export const matchSubmissionsWithCalls = (tenantId) =>
  api.post('/submissions/match-calls', null, { params: { tenant_id: tenantId } });



// ==================== SYSTEM REFRESH API ====================
export const refreshAllConfigurations = () =>
  api.post("/system/refresh-all");
export const getSystemStatus = () =>
  api.get("/system/status");

// ==================== LLM USAGE TRACKING API ====================
export const getLLMUsageSummary = (params = {}) =>
  api.get("/llm-usage/summary", { params });
export const getLLMUsageByProvider = (params = {}) =>
  api.get("/llm-usage/by-provider", { params });
export const getLLMUsageDaily = (days = 30, provider = null) =>
  api.get("/llm-usage/daily", { params: { days, provider } });



// ==================== QA ALERTS API ====================

// Get alerts for QA review
export const getQAAlerts = (params = {}) =>
  api.get("/qa-alerts/alerts", { params });

// Get alerts summary
export const getQAAlertsSummary = (params = {}) =>
  api.get("/qa-alerts/alerts/summary", { params });

// Review an alert
export const reviewAlert = (alertId, data = {}) =>
  api.post(`/qa-alerts/alerts/${alertId}/review`, data);

// Bulk review alerts
export const bulkReviewAlerts = (alertIds, qaNotes = null) =>
  api.post("/qa-alerts/alerts/bulk-review", { alert_ids: alertIds, qa_notes: qaNotes });

// Override alert compliance
export const overrideAlertCompliance = (alertId, data) =>
  api.post(`/qa-alerts/alerts/${alertId}/override`, data);

// Bulk override alerts compliance
export const bulkOverrideAlerts = (alertIds, newComplianceStatus, overrideReason, adjustScore = true, qaNotes = null) =>
  api.post("/qa-alerts/alerts/bulk-override", {
    alert_ids: alertIds,
    new_compliance_status: newComplianceStatus,
    override_reason: overrideReason,
    adjust_score: adjustScore,
    qa_notes: qaNotes
  });

// Archive reviewed alerts
export const archiveReviewedAlerts = (olderThanHours = 24) =>
  api.post("/qa-alerts/alerts/archive-reviewed", { older_than_hours: olderThanHours });

// Unarchive an alert
export const unarchiveAlert = (alertId) =>
  api.post(`/qa-alerts/alerts/${alertId}/unarchive`);

// Get QA activities
export const getQAActivities = (params = {}) =>
  api.get("/qa-alerts/activities", { params });

// Get auditor performance report
export const getAuditorPerformanceReport = (period = "30d", auditorId = null) =>
  api.get("/qa-alerts/reports/auditor-performance", { params: { period, auditor_id: auditorId } });

// Get compliance trends
export const getComplianceTrends = (period = "30d") =>
  api.get("/qa-alerts/reports/compliance-trends", { params: { period } });



// ==================== QA TASKS API ====================

// Get QA assignments
export const getQAAssignments = (tenantId) =>
  api.get("/qa-tasks/assignments", { params: { tenant_id: tenantId } });

// Get my assigned users (for auditors)
export const getMyAssignedUsers = (tenantId, auditorId, dateFilter = null) =>
  api.get("/qa-tasks/assignments/my-users", { params: { tenant_id: tenantId, auditor_id: auditorId, date_filter: dateFilter } });

// Create QA assignment
export const createQAAssignment = (tenantId, data) =>
  api.post("/qa-tasks/assignments", data, { params: { tenant_id: tenantId } });

// Update QA assignment
export const updateQAAssignment = (assignmentId, data) =>
  api.put(`/qa-tasks/assignments/${assignmentId}`, data);

// Delete QA assignment
export const deleteQAAssignment = (assignmentId) =>
  api.delete(`/qa-tasks/assignments/${assignmentId}`);

// Get QA tasks
export const getQATasks = (tenantId, auditorId = null, status = null) =>
  api.get("/qa-tasks/tasks", { params: { tenant_id: tenantId, auditor_id: auditorId, status: status } });

// Create QA task
export const createQATask = (tenantId, data) =>
  api.post("/qa-tasks/tasks", data, { params: { tenant_id: tenantId } });

// Update QA task
export const updateQATask = (taskId, data) =>
  api.put(`/qa-tasks/tasks/${taskId}`, data);

// Delete QA task
export const deleteQATask = (taskId) =>
  api.delete(`/qa-tasks/tasks/${taskId}`);

// Complete QA task
export const completeQATask = (taskId, data) =>
  api.post(`/qa-tasks/tasks/${taskId}/complete`, data);



// ==================== QA REVIEW API ====================

// QA Review a single call with mandatory note
export const qaReviewCall = (callId, qaNote, markCompliant = false) =>
  api.post(`/calls/${callId}/qa-review`, { qa_note: qaNote, mark_compliant: markCompliant });

// Bulk QA Review multiple calls
export const bulkQAReviewCalls = (callIds, qaNote, markCompliant = false) =>
  api.post('/calls/bulk-qa-review', { call_ids: callIds, qa_note: qaNote, mark_compliant: markCompliant });

// Get QA reviews for a call
export const getCallQAReviews = (callId) =>
  api.get(`/calls/${callId}/qa-reviews`);

// Get QA category stats for Alerts page cards
export const getAlertsCategoryStats = (tenantId, additionalParams = {}) =>
  api.get('/qa-alerts/alerts/category-stats', { params: { tenant_id: tenantId, ...additionalParams } });

// Get QA activity log for Alerts page
export const getQAActivityLog = (params = {}) =>
  api.get('/qa-alerts/activity-log', { params });

// Export QA activity log
export const exportQAActivityLog = (params = {}) =>
  api.get('/qa-alerts/activity-log/export', { params, responseType: 'blob' });

// Get QA Reviewed calls
export const getQAReviewedCalls = (params = {}) =>
  api.get('/qa-alerts/calls/reviewed', { params });

// Get Marked Compliant calls
export const getMarkedCompliantCalls = (params = {}) =>
  api.get('/qa-alerts/calls/marked-compliant', { params });

// Ride back (revert) a call's QA status
export const rideBackCallStatus = (callId, newStatus, reason, notes = null) =>
  api.post(`/qa-alerts/calls/${callId}/ride-back`, { new_status: newStatus, reason, notes });

// Bulk ride back multiple calls
export const bulkRideBackCalls = (callIds, newStatus, reason, notes = null) =>
  api.post('/qa-alerts/calls/bulk-ride-back', null, { 
    params: { call_ids: callIds, new_status: newStatus, reason, notes } 
  });

// Download call audio (admin-only)
export const downloadCallAudio = (callId) =>
  api.get(`/calls/${callId}/audio/download`, { responseType: 'blob' });

// Get audio download logs (admin-only)
export const getAudioDownloadLogs = (params = {}) =>
  api.get('/audio-download-logs', { params });

// ==================== COMMUNICATION COACH ====================

// Get coach configuration for tenant
export const getCoachConfig = () =>
  api.get('/communication-coach/config');

// Get user performance analysis
export const getUserPerformanceAnalysis = (userId = null, days = 30) =>
  api.get('/communication-coach/analysis', { params: { user_id: userId, days } });

// Update coach configuration
export const updateCoachConfig = (config) =>
  api.post('/communication-coach/config', config);

// Get user's communication profile
export const getCommunicationProfile = (userId = null) =>
  api.get('/communication-coach/user-profile', { params: { user_id: userId } });

// Get user's feedback context from call history
export const getUserFeedbackContext = (userId = null) =>
  api.get('/communication-coach/user-feedback-context', { params: { user_id: userId } });

// Get user's communication sessions
export const getCommunicationSessions = (userId = null, limit = 20, skip = 0) =>
  api.get('/communication-coach/sessions', { params: { user_id: userId, limit, skip } });

// Create new communication session
export const createCommunicationSession = (sessionType) =>
  api.post('/communication-coach/sessions', { session_type: sessionType });

// Get session details
export const getCommunicationSession = (sessionId) =>
  api.get(`/communication-coach/sessions/${sessionId}`);

// Send chat message to AI coach
export const sendCoachMessage = (sessionId, message, isAssessment = false) =>
  api.post('/communication-coach/chat', { session_id: sessionId, message, is_assessment: isAssessment });

// Transcribe audio
export const transcribeAudio = (audioBlob) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  return api.post('/communication-coach/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Text to speech
export const textToSpeech = (text) =>
  api.post('/communication-coach/tts', null, { params: { text } });

// Complete session
export const completeCommunicationSession = (sessionId, transcript = '') =>
  api.post(`/communication-coach/sessions/${sessionId}/complete`, { transcript });

// Get user progress
export const getCommunicationProgress = (userId = null) =>
  api.get('/communication-coach/progress', { params: { user_id: userId } });

// Get team analytics
export const getCommunicationAnalytics = (department = null) =>
  api.get('/communication-coach/analytics', { params: { department } });

// Upload session recording to OneDrive
export const uploadSessionRecording = (sessionId, audioBlob) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  return api.post(`/communication-coach/sessions/${sessionId}/upload-recording`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Communication Coach - Gamification APIs
export const getBadgeDefinitions = () =>
  api.get('/communication-coach/badges/definitions');

export const getUserBadges = (userId = null) =>
  api.get('/communication-coach/badges/user', { params: { user_id: userId } });

export const checkBadges = () =>
  api.post('/communication-coach/badges/check');

export const getBadgesLeaderboard = (limit = 20) =>
  api.get('/communication-coach/badges/leaderboard', { params: { limit } });

export const getUserChallenges = (includeCompleted = false) =>
  api.get('/communication-coach/challenges', { params: { include_completed: includeCompleted } });

export const createChallenge = (challengeData) =>
  api.post('/communication-coach/challenges', challengeData);

export const updateChallenge = (challengeId, challengeData) =>
  api.put(`/communication-coach/challenges/${challengeId}`, challengeData);

export const deleteChallenge = (challengeId) =>
  api.delete(`/communication-coach/challenges/${challengeId}`);

export const updateChallengeProgress = (challengeId) =>
  api.post(`/communication-coach/challenges/${challengeId}/update-progress`);

export const getAdminChallenges = (includeInactive = false) =>
  api.get('/communication-coach/challenges/admin', { params: { include_inactive: includeInactive } });

// Communication Coach - Reports API
export const getCoachReportsSummary = (params = {}) =>
  api.get('/communication-coach/reports/summary', { params });

export const exportCoachReportsCSV = (reportType = 'user_performance', startDate = null, endDate = null) =>
  api.get('/communication-coach/reports/export/csv', { 
    params: { report_type: reportType, start_date: startDate, end_date: endDate },
    responseType: 'blob'
  });

export const exportCoachReportsPDF = (reportType = 'user_performance', startDate = null, endDate = null) =>
  api.get('/communication-coach/reports/export/pdf', { 
    params: { report_type: reportType, start_date: startDate, end_date: endDate }
  });

export const getUserDetailedReport = (userId) =>
  api.get(`/communication-coach/reports/user/${userId}`);


// ==================== TERMS & CONDITIONS API ====================

// Check T&C status for user
export const checkTCStatus = (userId, tenantId) =>
  api.get('/terms-conditions/status', { params: { user_id: userId, tenant_id: tenantId } });

// Get T&C content
export const getTCContent = (tenantName) =>
  api.get('/terms-conditions/content', { params: { tenant_name: tenantName } });

// Accept T&C
export const acceptTermsConditions = (data) =>
  api.post('/terms-conditions/accept', data);

// Get all T&C agreements for tenant (admin)
export const getTCAgreements = (tenantId, page = 1, limit = 50) =>
  api.get('/terms-conditions/agreements', { params: { tenant_id: tenantId, page, limit } });

// Download T&C agreement PDF
export const downloadTCAgreementPDF = (agreementId) =>
  api.get(`/terms-conditions/agreements/${agreementId}/pdf`, { responseType: 'blob' });

// Get user's own T&C agreement
export const getMyTCAgreement = (userId, tenantId) =>
  api.get('/terms-conditions/my-agreement', { params: { user_id: userId, tenant_id: tenantId } });


// ==================== CALL PROCESSING GUARDIAN API ====================

// Get guardian health status
export const getGuardianHealth = () =>
  api.get('/calls/guardian/health');

// Get guardian metrics
export const getGuardianMetrics = () =>
  api.get('/calls/guardian/metrics');

// Trigger manual repair cycle
export const triggerGuardianRepair = () =>
  api.post('/calls/guardian/repair');

// Get quarantined calls
export const getQuarantinedCalls = (skip = 0, limit = 50) =>
  api.get('/calls/guardian/quarantined', { params: { skip, limit } });

// Quarantine a call manually
export const quarantineCall = (callId, reason) =>
  api.post(`/calls/guardian/quarantine/${callId}`, null, { params: { reason } });

// Unquarantine a call
export const unquarantineCall = (callId) =>
  api.post(`/calls/guardian/unquarantine/${callId}`);

// Get call audit trail for guardian (uses different endpoint)
export const getGuardianCallAuditTrail = (callId) =>
  api.get(`/calls/${callId}/audit-trail`);

